import { BREAK_TYPES } from '../../constants/breakConstants';
import { formatDurationBetweenTimestamps } from '../../formatting/timeFormatting';
import { fetchLaborLawsForVenue } from '../laborLaws/laborLawsHelpers';
import { DEFAULT_MEAL_BREAK_DURATION, DEFAULT_REST_BREAK_DURATION } from '../laborLaws/laborLawsHelpers';
import { convertMinutesToMilliseconds } from '../timeUtils';

/**
 * Generates descriptions for break times
 * @param {Object} break - The break object containing all break data
 * @param {Function} formatTime - Function to format time in venue timezone
 * @param {Function} formatTimeRange - Function to format time range in venue timezone
 * @returns {Object} Status configuration for the break
 */
export const getBreakSubtitleData = (breakData, formatTime = null, formatTimeRange = null) => {
  const { start, end, scheduledStart, scheduledEnd, mustStartBefore, waived } = breakData;
  const now = Date.now();

  // Determine the break state
  let breakState = 'default';

  if (waived) {
    breakState = 'waived';
  } else if (start && end) {
    breakState = 'completed';
  } else if (start) {
    breakState = 'inProgress';
  } else if (
    scheduledStart &&
    mustStartBefore &&
    new Date(scheduledStart).getTime() < now &&
    new Date(mustStartBefore).getTime() > now
  ) {
    breakState = 'deadlineApproaching';
  } else if (scheduledStart && scheduledEnd) {
    breakState = 'scheduled';
  }

  switch (breakState) {
    case 'completed':
      return {
        breakSubtitle: formatTimeRange ? formatTimeRange(start, end) : '',
        breakStatus: 'success',
      };
    case 'inProgress':
      return {
        breakSubtitle: formatTime ? `Started at ${formatTime(start)}` : '',
        breakStatus: 'info',
      };
    case 'deadlineApproaching':
      return {
        breakSubtitle: formatTime ? `Must start by ${formatTime(new Date(mustStartBefore))}` : '',
        breakStatus: 'warning',
      };
    case 'scheduled':
      return {
        breakSubtitle: formatTime ? `Tempo Suggestion: ${formatTime(new Date(scheduledStart))}` : '',
        breakStatus: 'tempo-suggestion',
      };
    case 'waived':
      return {
        breakSubtitle: formatTime ? `Tempo Suggestion: ${formatTime(new Date(scheduledStart))}` : '',
        breakStatus: 'tempo-suggestion',
      };
    default:
      return { breakSubtitle: '', breakStatus: 'info' };
  }
};

/**
 * Generates display data for break status and duration
 * @param {Object} break - The break object containing all break data
 * @param {Function} formatDuration - Function to format duration between timestamps
 * @returns {Object} Display configuration for the break status
 */
export const getBreakStatusDisplayData = (breakData) => {
  const { start, end, mustStartBefore, scheduledStart, waived, type } = breakData;
  const now = Date.now();

  const isMealBreak = type === BREAK_TYPES.MEAL;
  // Check if the break's scheduled time has passed (for rest breaks)
  const hasScheduledTimePassed = scheduledStart && new Date(scheduledStart).getTime() < now;

  // Check if the break deadline has passed (for meal breaks)
  const hasBreakDeadlinePassed = mustStartBefore && new Date(mustStartBefore).getTime() < now;

  // Determine the break display state
  let breakDisplayState = 'default';
  if (waived) {
    breakDisplayState = 'waived';
  } else if (start && end) {
    breakDisplayState = 'completed';
  } else if (start && !end) {
    breakDisplayState = 'inProgress';
  } else if (!start && !(isMealBreak ? hasBreakDeadlinePassed : hasScheduledTimePassed)) {
    breakDisplayState = 'upcoming';
  } else if (!start && (hasScheduledTimePassed || hasBreakDeadlinePassed)) {
    breakDisplayState = 'missed';
  }

  switch (breakDisplayState) {
    case 'waived':
      return {
        status: 'warning',
        text: 'Waived',
      };
    case 'completed':
      return {
        status: 'success',
        text: formatDurationBetweenTimestamps(start, end),
      };
    case 'inProgress':
      return {
        status: 'warning',
        text: 'In progress',
      };
    case 'upcoming':
      return {
        status: 'info',
        text: 'Upcoming',
      };
    case 'missed':
      return {
        status: 'error',
        text: 'Missed',
      };
    default:
      return {
        status: 'info',
        text: 'Unknown',
      };
  }
};

export const getLegalEndTime = async (breakItem, vid) => {
  // fetch the state's labor laws to get the mealBreakDuration and restBreakDuration
  const laborLawsResponse = await fetchLaborLawsForVenue(vid);

  if (!laborLawsResponse) {
    throw new Error('Failed to fetch labor laws from backend');
  }

  const { laws } = laborLawsResponse;

  const { meal_break_duration, rest_break_duration } = laws;

  const startTime = new Date(breakItem.start);

  const breakDuration =
    breakItem.type === BREAK_TYPES.MEAL
      ? meal_break_duration || DEFAULT_MEAL_BREAK_DURATION
      : rest_break_duration || DEFAULT_REST_BREAK_DURATION;

  const legalEndTime = new Date(startTime.getTime() + convertMinutesToMilliseconds(breakDuration));

  return legalEndTime;
};
